import {
  ColumnFiltersState,
  ExpandedState,
  PaginationState,
  SortingState,
} from '@tanstack/react-table';
import { DEFAULT_PAGE_SIZE } from 'utils/page-sizes';
import { DataTableSelectionState } from './types';

// ─────────────────────────────────────
// State Shape
// ─────────────────────────────────────

/**
 * Transient (non-persisted) state managed by `useTableState`'s reducer.
 * Persisted column state (visibility, order, pinning, sizing, filter-active)
 * lives in `useTablePersistence` via atomWithStorage — intentionally NOT in
 * this reducer. See phase 5 plan + roadmap for the architectural rationale.
 */
export interface TableTransientState {
  globalFilter: string;
  columnFilters: ColumnFiltersState;
  sorting: SortingState;
  pagination: PaginationState;
  expanded: ExpandedState;
  /**
   * Canonical AG-Grid selection shape (unified in phase 4).
   * - selectAll=true → all rows selected, except `toggledNodes` (exception list)
   * - selectAll=false → no rows selected, except `toggledNodes` (selected list)
   * Client mode never sets selectAll=true by construction — the select-all
   * header handler enumerates filtered row IDs into `toggledNodes` directly.
   */
  selection: {
    selectAll: boolean;
    toggledNodes: Set<string>;
  };
  isFilterPanelOpen: boolean;
}

// ─────────────────────────────────────
// Actions
// ─────────────────────────────────────

export type TableAction =
  | { type: 'SET_GLOBAL_FILTER'; payload: string }
  | { type: 'SET_COLUMN_FILTERS'; payload: ColumnFiltersState }
  | { type: 'SET_SORTING'; payload: SortingState }
  | { type: 'SET_PAGINATION'; payload: PaginationState }
  | { type: 'SET_EXPANDED'; payload: ExpandedState }
  | {
      type: 'SET_SELECTION';
      payload: { selectAll: boolean; toggledNodes: Set<string> };
    }
  | { type: 'SET_FILTER_PANEL_OPEN'; payload: boolean }
  | { type: 'RESET_ALL_FILTERS' };

// ─────────────────────────────────────
// Initial State Factory
// ─────────────────────────────────────

export interface InitialTransientStateInput {
  initialGlobalFilter?: string;
  initialColumnFilters?: ColumnFiltersState;
  initialSortColumn?: string;
  initialSortDirection?: 'asc' | 'desc';
  initialPage?: number;
  initialPageSize?: number;
  initialExpanded?: ExpandedState;
  initialSelection?: DataTableSelectionState;
}

/**
 * Build the reducer's initial state from `useTableState`'s init props.
 * Passed as the 3rd `useReducer` arg (lazy initializer) so it runs once on mount.
 *
 * @param {InitialTransientStateInput} input - Initial values from hook props
 * @return {TableTransientState} The seeded transient state
 */
export function buildInitialState(
  input: InitialTransientStateInput
): TableTransientState {
  return {
    globalFilter: input.initialGlobalFilter ?? '',
    columnFilters: input.initialColumnFilters ?? [],
    sorting: input.initialSortColumn
      ? [
          {
            id: input.initialSortColumn,
            desc: input.initialSortDirection === 'desc',
          },
        ]
      : [],
    pagination: {
      pageIndex: (input.initialPage ?? 1) - 1,
      pageSize: input.initialPageSize ?? DEFAULT_PAGE_SIZE,
    },
    expanded: input.initialExpanded ?? {},
    selection: {
      selectAll: input.initialSelection?.selectAll ?? false,
      toggledNodes: new Set(input.initialSelection?.toggledNodes ?? []),
    },
    isFilterPanelOpen: true,
  };
}

// ─────────────────────────────────────
// Reducer
// ─────────────────────────────────────

/**
 * Returns state with `pagination.pageIndex` reset to 0. Preserves object
 * identity when already on page 1 — saves a render per keystroke after page 1.
 *
 * @param {TableTransientState} state - The state to reset pagination in
 * @return {TableTransientState} State with pagination reset (or same reference if already on page 1)
 */
function resetPage(state: TableTransientState): TableTransientState {
  if (state.pagination.pageIndex === 0) return state;
  return {
    ...state,
    pagination: { ...state.pagination, pageIndex: 0 },
  };
}

/**
 * Pure reducer for `useTableState`'s transient state. Cross-slice rules
 * (filter/sort/search changes reset page, RESET_ALL_FILTERS clears both
 * filters + resets page) are baked in as atomic transitions — no risk of
 * mid-flight observable inconsistency from sequential setStates.
 *
 * Observer callbacks (`onFilterChange`, `fireLoadData`, etc.) fire from the
 * wrapped setters in `useTableState`, NOT inside this reducer. Reducer stays
 * pure for testability.
 *
 * @param {TableTransientState} state - Current state
 * @param {TableAction} action - Action to apply
 * @return {TableTransientState} New state
 */
export function tableReducer(
  state: TableTransientState,
  action: TableAction
): TableTransientState {
  switch (action.type) {
    case 'SET_GLOBAL_FILTER':
      return resetPage({ ...state, globalFilter: action.payload });
    case 'SET_COLUMN_FILTERS':
      return resetPage({ ...state, columnFilters: action.payload });
    case 'SET_SORTING':
      return resetPage({ ...state, sorting: action.payload });
    case 'SET_PAGINATION':
      return { ...state, pagination: action.payload };
    case 'SET_EXPANDED':
      return { ...state, expanded: action.payload };
    case 'SET_SELECTION':
      return { ...state, selection: action.payload };
    case 'SET_FILTER_PANEL_OPEN':
      return { ...state, isFilterPanelOpen: action.payload };
    case 'RESET_ALL_FILTERS':
      return resetPage({
        ...state,
        globalFilter: '',
        columnFilters: [],
      });
  }
}
